import { Button, Flex, Image, ModalBody } from "@chakra-ui/react"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { FormattedMessage, useIntl } from "react-intl"

import excelLogo from "@/assets/images/excel-logo.svg"
import { ArrowToBottomIcon } from "@/components/Icons"
import { Modal } from "@/components/Modal"
import { P2 } from "@/components/Typographies"
import { useAppToast } from "@/hooks/useAppToast"
import { getInterBuExcelExportData } from "@/networking/api/sharedData/interBuExcelExport"

import { buildInterBuExcelRows } from "./helpers/usePayloadToExcelDataAdapter"

type Props = {
  isOpen: boolean
  onClose: () => void
}

export const ExportInterBUModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { handleSubmit } = useForm()
  const toast = useAppToast()
  const intl = useIntl()
  const [isLoadingExcel, setIsLoadingExcel] = useState<boolean>(false)

  const onSubmit = async () => {
    setIsLoadingExcel(true)
    try {
      const [frData, enData] = await Promise.all([
        getInterBuExcelExportData("fr_FR"),
        getInterBuExcelExportData("en_GB"),
      ])
      const frRows = buildInterBuExcelRows(frData, "fr")
      const enRows = buildInterBuExcelRows(enData, "en")

      if (frRows.length === 0) {
        toast({
          status: "info",
          title: intl.formatMessage({ id: "userSharedData.interbu.export.empty" }),
        })
        return
      }

      const { utils, writeFileXLSX } = await import("xlsx")
      const workbook = utils.book_new()
      utils.book_append_sheet(workbook, utils.json_to_sheet(frRows), "FR")
      utils.book_append_sheet(workbook, utils.json_to_sheet(enRows), "EN")
      writeFileXLSX(workbook, `interbu-export-${new Date().toISOString().replace(/:/g, "-")}.xlsx`)
      toast({
        status: "success",
        title: intl.formatMessage({ id: "export.exportModal.excel.downloadSuccessful" }),
      })
      onClose()
    } catch {
      toast({
        status: "error",
        title: intl.formatMessage({ id: "userSharedData.interbu.export.error" }),
      })
    } finally {
      setIsLoadingExcel(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={<FormattedMessage id="export.exportModal.title" />}
    >
      <ModalBody mb="50px">
        <Flex
          as="form"
          flexDir="column"
          alignItems="center"
          gap={8}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Flex gap={2} alignItems="center">
            <P2>
              <FormattedMessage id="export.exportModal.excel.title" />
            </P2>
            <Image src={excelLogo} alt="Excel logo" h="16px" />
          </Flex>

          <Button display="flex" gap={2} type="submit" isLoading={isLoadingExcel}>
            <FormattedMessage id="export.exportModal.excel.download" />
            <ArrowToBottomIcon boxSize={4} />
          </Button>
        </Flex>
      </ModalBody>
    </Modal>
  )
}
